import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError, setToken } from '../lib/api';

/**
 * Sign in, by password or by emailed link.
 *
 * The link is not a fallback — it is the account recovery path. Someone who has
 * forgotten their password, or who has been locked out by somebody else's
 * guessing, gets in through their inbox and sets a new one from Settings. That
 * is why there is no separate "forgot password" flow anywhere in this app.
 *
 * The "check your email" state is deliberately identical whether or not the
 * address has an account. The server does not say, and neither should the UI,
 * because a form that reveals who has an account is a form that leaks a
 * membership list.
 */
export function Login() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [mode, setMode] = useState<'password' | 'link'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'password') {
        const { token } = await api.signInWithPassword(email.trim(), password);
        setToken(token);
        await qc.invalidateQueries();
        navigate('/dashboard', { replace: true });
        return;
      }
      const res = await api.requestLink(email);
      setSent(true);
      setDevLink(res.devLink ?? null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the exchange.');
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div className="card login">
        <h2>Check your email</h2>
        <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink-2)' }}>
          If <b>{email}</b> can sign in, a link is on its way. It works once and expires in 15 minutes.
        </p>
        {devLink && (
          <div className="notice ok" style={{ wordBreak: 'break-all' }}>
            <b>Development mode</b> — no mail provider is configured, so here is the link:
            <br />
            <a href={devLink} style={{ color: 'var(--yes)', textDecoration: 'underline' }}>
              {devLink}
            </a>
          </div>
        )}
        <button style={{ marginTop: 16 }} onClick={() => setSent(false)}>
          Use a different address
        </button>
      </div>
    );
  }

  return (
    <div className="card login">
      <h2>Sign in</h2>
      <form onSubmit={submit}>
        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </div>

        {mode === 'password' && (
          <div className="field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        )}

        <button
          className="submit"
          data-side="YES"
          disabled={busy || email.length === 0 || (mode === 'password' && password.length === 0)}
        >
          {busy ? (mode === 'password' ? 'Signing in…' : 'Sending…') : mode === 'password' ? 'Sign in' : 'Email me a link'}
        </button>
        {error && <div className="notice error">{error}</div>}
      </form>

      <div style={{ marginTop: 16, fontSize: 13.5, color: 'var(--ink-3)', lineHeight: 1.6 }}>
        {mode === 'password' ? (
          <>
            <button
              type="button"
              style={{ padding: 0, background: 'none', border: 'none', color: 'var(--yes)', cursor: 'pointer', font: 'inherit' }}
              onClick={() => {
                setMode('link');
                setError(null);
              }}
            >
              Forgotten your password, or locked out?
            </button>{' '}
            We'll email you a link instead.
          </>
        ) : (
          <>
            We'll email a one-time link. It works once and expires in 15 minutes.{' '}
            <button
              type="button"
              style={{ padding: 0, background: 'none', border: 'none', color: 'var(--yes)', cursor: 'pointer', font: 'inherit' }}
              onClick={() => {
                setMode('password');
                setError(null);
              }}
            >
              Use a password instead.
            </button>
          </>
        )}
      </div>

      <p style={{ fontSize: 13.5, color: 'var(--ink-3)', marginTop: 14, marginBottom: 0 }}>
        No account yet? <Link to="/signup" style={{ color: 'var(--yes)' }}>Create one</Link> — you start
        with 1,000.00 play credits.
      </p>
    </div>
  );
}

/** Landing page for an emailed link: exchange the token for a session. */
export function SignIn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const token = params.get('token');

  useEffect(() => {
    if (!token) {
      setError('That link is missing its sign-in code.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { token: session } = await api.redeem(token);
        if (cancelled) return;
        setToken(session);
        await qc.invalidateQueries();
        navigate('/dashboard', { replace: true });
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not sign you in.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, navigate, qc]);

  if (error) {
    return (
      <div className="card login">
        <h2>Sign-in failed</h2>
        <div className="notice error">{error}</div>
        <button style={{ marginTop: 16 }} onClick={() => navigate('/login', { replace: true })}>
          Request a new link
        </button>
      </div>
    );
  }

  return <div className="empty-state">Signing you in…</div>;
}
